import { FEATURES } from "../../config/features";

// NOTE: Path assumes this file is at src/utils/employee/resolveField.js
// Adjust the import path if your features.js is located elsewhere

// =============================================================================
// FIELD DEFAULTS
// Applied to every field that does not explicitly define the property.
// Only override these in the config when you need different behavior.
// =============================================================================
const FIELD_DEFAULTS = {
  type:         "text",
  required:     false,
  feature:      null,         // null = always visible
  displayKey:   "description",// most lookups use this property for display
  valueKey:     "id",         // most lookups use id as the option value
  refKey:       null,         // null = not a dropdown
  dropdownList: null,         // null = no excel dropdown needed
  audit:        true,         // true = included in audit trail comparison
  width:        20,           // excel column width default
  sampleValue:  "<VALUE>",    // excel template sample value default
};

// =============================================================================
// RESOLVE FIELD
// Takes a raw field definition from the config and returns a fully resolved
// field object — all functions evaluated, all defaults applied, visibility set.
//
// Usage:
//   const resolved = resolveField(field);
//   if (!resolved.visible) return null;
//   console.log(resolved.label);      // always a string
//   console.log(resolved.displayKey); // always a string
// =============================================================================
export const resolveField = (field) => {
  // Apply defaults first, then override with what the field explicitly defines
  const withDefaults = { ...FIELD_DEFAULTS, ...field };

  // Resolve anything that can be a function
  // These receive FEATURES so the field can vary behavior per client
  const label = typeof withDefaults.label === "function"
    ? withDefaults.label(FEATURES)
    : withDefaults.label;

  const displayKey = typeof withDefaults.displayKey === "function"
    ? withDefaults.displayKey(FEATURES)
    : withDefaults.displayKey;

  const sampleValue = typeof withDefaults.sampleValue === "function"
    ? withDefaults.sampleValue(FEATURES)
    : withDefaults.sampleValue;

  // Determine visibility from feature flag
  // null feature = always visible
  // string feature = visible only when FEATURES[feature] is truthy
  const visible = withDefaults.feature
    ? !!FEATURES[withDefaults.feature]
    : true;

  // Pre-bind FEATURES into dropdownList.data()
  // Before: field.dropdownList.data(refs, FEATURES)  ← consumer needs FEATURES
  // After:  field.dropdownList.data(refs)             ← FEATURES already baked in
  const dropdownList = withDefaults.dropdownList
    ? {
        ...withDefaults.dropdownList,
        data: (refs) => withDefaults.dropdownList.data(refs, FEATURES),
      }
    : null;

  return {
    ...withDefaults,
    label,
    displayKey,
    sampleValue,
    visible,
    dropdownList,
  };
};

// =============================================================================
// RESOLVE SECTION
// Takes a raw section definition from the config and returns whether the
// entire section is visible, plus all its resolved fields.
//
// A section can be hidden entirely via a section-level feature flag.
// e.g. employeeSeamanBookDetail has feature: "EMPLOYEE_ACCOUNT_SEAMAN_BOOK"
// When that flag is off, the entire section and all its fields are hidden.
//
// Usage:
//   const resolved = resolveSection(EMPLOYEE_CONFIG.employeeSeamanBookDetail);
//   if (!resolved.visible) return; // skip entire worksheet
//   resolved.fields.forEach(f => ...); // all fields already resolved
// =============================================================================
export const resolveSection = (section) => {
  // Check section-level feature flag first
  const sectionVisible = section.feature
    ? !!FEATURES[section.feature]
    : true;

  // If the section itself is hidden, no need to resolve individual fields
  if (!sectionVisible) {
    return {
      ...section,
      visible: false,
      fields: [],
      visibleFields: [],
    };
  }

  // Resolve each field — field-level feature flags are checked inside resolveField
  const resolvedFields = section.fields.map(resolveField);

  return {
    ...section,
    visible: true,
    fields: resolvedFields,
    // Convenience — only the fields that are actually visible
    visibleFields: resolvedFields.filter((f) => f.visible),
  };
};